import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, FileText, Pin, Search, X, Filter, Users, GripVertical, ChevronLeft, ChevronRight, Settings, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import NoteEditor from "@/components/note-editor";
import MultiTagSelector from "@/components/multi-tag-selector";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import type { Note } from "@shared/schema";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuickCreateRoute } from "@/hooks/useQuickCreateRoute";

export default function NotepadPage() {
  const { toast } = useToast();
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  useQuickCreateRoute("note", () => setIsCreating(true));
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [activeTab, setActiveTab] = useState<"my-notes" | "shared">("my-notes");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tagManagementOpen, setTagManagementOpen] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const queryClient = useQueryClient();

  const { data: notes = [], isLoading } = useQuery<Note[]>({
    queryKey: ["/api/notes", searchTerm, selectedTags.join(","), pinnedOnly],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (selectedTags.length > 0) params.append("tags", selectedTags.join(","));
      if (pinnedOnly) params.append("pinnedOnly", "true");
      
      const response = await fetch(`/api/notes?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch notes');
      return response.json();
    },
  });

  const { data: allTags = [] } = useQuery<string[]>({
    queryKey: ["/api/notes/tags"],
    queryFn: async () => {
      const allNotes = await fetch('/api/notes').then(r => r.json());
      const tagSet = new Set<string>();
      allNotes.forEach((note: Note) => {
        note.tags.forEach(tag => tagSet.add(tag));
      });
      return Array.from(tagSet).sort();
    },
  });

  const { data: sharedNotes = [] } = useQuery<any[]>({
    queryKey: ["/api/shares/notes/received"],
    enabled: activeTab === "shared",
  });

  const createNoteMutation = useMutation({
    mutationFn: async (noteData: { title: string; content: any; plainContent: string; tags: string[]; type?: string; researchQuestion?: string }) => {
      const response = await fetch("/api/notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(noteData),
      });
      if (!response.ok) throw new Error('Failed to create note');
      return response.json();
    },
    onSuccess: (newNote) => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notes/tags"] });
      setSelectedNote(newNote);
      setIsCreating(false);
      toast({
        title: "Note created",
        description: "Your note has been saved successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Save failed",
        description: "Failed to create note. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Note> }) => {
      const response = await fetch(`/api/notes/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error('Failed to update note');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notes/tags"] });
      setSelectedNote(null);
      setIsCreating(false);
      toast({
        title: "Note saved",
        description: "Your changes have been saved.",
      });
    },
    onError: () => {
      toast({
        title: "Save failed",
        description: "Failed to save note. Please try again.",
        variant: "destructive",
      });
    },
  });

  const isSaving = createNoteMutation.isPending || updateNoteMutation.isPending;

  const deleteNoteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/notes/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error('Failed to delete note');
      if (response.status === 204) return;
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notes/tags"] });
      setSelectedNote(null);
      setIsCreating(false);
      toast({
        title: "Note deleted",
        description: "Note has been successfully deleted.",
      });
    },
    onError: () => {
      toast({
        title: "Delete failed",
        description: "Failed to delete note. Please try again.",
        variant: "destructive",
      });
    },
  });

  const reorderNotesMutation = useMutation({
    mutationFn: async (updates: Array<{ id: string; displayOrder: number }>) => {
      const response = await fetch('/api/notes/reorder', {
        method: 'POST',
        body: JSON.stringify({ updates }),
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Reorder failed');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes"] });
    },
  });

  const removeTagFromAllNotesMutation = useMutation({
    mutationFn: async (tagToRemove: string) => {
      const allNotesResponse = await fetch('/api/notes');
      if (!allNotesResponse.ok) throw new Error('Failed to fetch notes');
      const allNotes: Note[] = await allNotesResponse.json();
      
      const notesWithTag = allNotes.filter(note => note.tags.includes(tagToRemove));
      
      for (const note of notesWithTag) {
        const updatedTags = note.tags.filter(t => t !== tagToRemove);
        const response = await fetch(`/api/notes/${note.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tags: updatedTags }),
        });
        if (!response.ok) throw new Error(`Failed to update note ${note.id}`);
      }
      
      return { removedTag: tagToRemove, updatedCount: notesWithTag.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notes/tags"] });
      setSelectedTags(prev => prev.filter(t => t !== data.removedTag));
      toast({
        title: "Tag removed",
        description: `Removed "${data.removedTag}" from ${data.updatedCount} note${data.updatedCount !== 1 ? 's' : ''}.`,
      });
    },
    onError: () => {
      toast({
        title: "Failed to remove tag",
        description: "Could not remove the tag. Please try again.",
        variant: "destructive",
      });
    },
  });

  const addCustomTagMutation = useMutation({
    mutationFn: async (tagName: string) => {
      const dummyNote = {
        title: `Tag: ${tagName}`,
        content: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'This note was created to register the tag.' }] }] },
        plainContent: 'This note was created to register the tag.',
        tags: [tagName],
      };
      
      const response = await fetch('/api/notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dummyNote),
      });
      
      if (!response.ok) throw new Error('Failed to create tag');
      const createdNote = await response.json();
      
      await fetch(`/api/notes/${createdNote.id}`, { method: 'DELETE' });
      
      return { tagName };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notes/tags"] });
      setNewTagName("");
      toast({
        title: "Tag added",
        description: `Tag "${data.tagName}" is now available for use.`,
      });
    },
    onError: () => {
      toast({
        title: "Failed to add tag",
        description: "Could not add the tag. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleCreateNew = () => {
    setSelectedNote(null);
    setIsCreating(true);
  };

  const handleSaveNote = (data: { title: string; content: any; plainContent: string; tags: string[]; type?: string; researchQuestion?: string }) => {
    if (isCreating) {
      createNoteMutation.mutate(data);
    } else if (selectedNote) {
      updateNoteMutation.mutate({
        id: selectedNote.id,
        updates: data,
      });
    }
  };

  const handleDeleteNote = () => {
    if (selectedNote) {
      deleteNoteMutation.mutate(selectedNote.id);
    }
  };

  const handleCancelEdit = () => {
    setSelectedNote(null);
    setIsCreating(false);
  };

  const handleNoteClick = (note: Note) => {
    setSelectedNote(note);
    setIsCreating(false);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedTags([]);
    setPinnedOnly(false);
  };

  const hasActiveFilters = searchTerm || selectedTags.length > 0 || pinnedOnly;

  // Sort notes: pinned first, then by displayOrder, then by creation date
  const sortedNotes = [...notes].sort((a, b) => {
    // Pinned items always on top
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    
    // Within pinned or unpinned groups, sort by displayOrder
    const orderA = a.displayOrder ?? 0;
    const orderB = b.displayOrder ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    
    // If same displayOrder, sort by creation date (newest first)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  // Sortable Note Card Component
  function SortableNoteCard({ note }: { note: Note }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: note.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div ref={setNodeRef} style={style}>
        <Card
          className={`cursor-pointer transition-colors hover:bg-accent ${
            selectedNote?.id === note.id ? "ring-2 ring-primary" : ""
          }`}
          onClick={() => handleNoteClick(note)}
          data-testid={`note-card-${note.id}`}
        >
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              {/* Drag handle */}
              <div
                {...attributes}
                {...listeners}
                className="mt-0.5 cursor-grab active:cursor-grabbing touch-manipulation"
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-medium text-sm truncate flex-1">{note.title}</h3>
                  {note.isPinned && <Pin className="h-3 w-3 text-yellow-500 flex-shrink-0" />}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                  {getContentPreview(note.content)}
                </p>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatDistanceToNow(new Date(note.updatedAt), { addSuffix: true })}</span>
                  {note.tags.length > 0 && (
                    <div className="flex gap-1 flex-wrap max-w-[60%]">
                      {note.tags.slice(0, 2).map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs px-1 py-0">
                          {tag}
                        </Badge>
                      ))}
                      {note.tags.length > 2 && (
                        <Badge variant="outline" className="text-xs px-1 py-0">
                          +{note.tags.length - 2}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = sortedNotes.findIndex(note => note.id === active.id);
    const newIndex = sortedNotes.findIndex(note => note.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const movedNote = sortedNotes[oldIndex];
    const targetNote = sortedNotes[newIndex];

    // Prevent moving unpinned items into pinned section and vice versa
    if (movedNote.isPinned !== targetNote.isPinned) {
      toast({
        title: "Cannot move",
        description: "Pinned and unpinned notes must stay in their respective sections.",
        variant: "destructive",
      });
      return;
    }

    // Create new array with reordered items
    const newSortedNotes = [...sortedNotes];
    newSortedNotes.splice(oldIndex, 1);
    newSortedNotes.splice(newIndex, 0, movedNote);

    // Calculate new display orders for all items in the same pinned/unpinned group
    const updates: Array<{ id: string; displayOrder: number }> = [];
    let displayOrder = 0;

    newSortedNotes.forEach(note => {
      if (note.isPinned === movedNote.isPinned) {
        updates.push({ id: note.id, displayOrder });
        displayOrder++;
      }
    });

    // Optimistically update the UI
    queryClient.setQueryData<Note[]>(
      ["/api/notes", searchTerm, selectedTags.join(","), pinnedOnly],
      (oldNotes) => {
        if (!oldNotes) return oldNotes;
        return oldNotes.map(note => {
          const update = updates.find(u => u.id === note.id);
          return update ? { ...note, displayOrder: update.displayOrder } : note;
        });
      }
    );

    // Persist to backend
    reorderNotesMutation.mutate(updates);
  };

  const getContentPreview = (content: any): string => {
    if (typeof content === 'string') {
      return content.slice(0, 50);
    }
    if (content && typeof content === 'object') {
      if (content.text) return content.text.slice(0, 50);
      if (content.ops) {
        const text = content.ops.map((op: any) => op.insert || '').join('');
        return text.slice(0, 50);
      }
    }
    return '';
  };

  return (
    <div className="h-full w-full flex flex-col lg:flex-row gap-4 p-4 lg:p-6">
      {/* Left Sidebar - Notes List */}
      {/* Hide on mobile when editing/creating, always show on desktop */}
      <div className={`flex flex-col gap-4 h-full transition-all duration-300 ease-in-out relative ${
        (selectedNote || isCreating) ? 'hidden lg:flex' : 'flex'
      } ${sidebarCollapsed ? 'w-full lg:w-20' : 'w-full lg:w-[30%]'}`}>
        
        {/* Collapse/Expand Toggle Button - Desktop only */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:flex absolute -right-3 top-6 z-10 h-6 w-6 rounded-full border bg-background shadow-md hover:bg-accent"
          data-testid="button-toggle-notepad-sidebar"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>

        <Card className="flex-1 flex flex-col overflow-hidden">
          {/* Collapsed View */}
          {sidebarCollapsed ? (
            <div className="flex flex-col h-full">
              {/* Collapsed Header */}
              <div className="flex flex-col items-center py-3 gap-2 border-b">
                <Button
                  size="icon"
                  onClick={handleCreateNew}
                  data-testid="button-create-note-collapsed"
                  className="h-9 w-9"
                  title="New Note"
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setSidebarCollapsed(false)}
                  data-testid="button-search-collapsed"
                  className="h-9 w-9"
                  title="Search & Filter"
                >
                  <Search className="h-4 w-4" />
                </Button>
                {activeTab === "my-notes" && (
                  <Button
                    size="icon"
                    variant={pinnedOnly ? "secondary" : "ghost"}
                    onClick={() => setPinnedOnly(!pinnedOnly)}
                    data-testid="button-pinned-collapsed"
                    className="h-9 w-9"
                    title="Pinned Only"
                  >
                    <Pin className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              {/* Collapsed Tab Toggle */}
              <div className="flex border-b">
                <button
                  onClick={() => setActiveTab("my-notes")}
                  className={`flex-1 py-2 flex justify-center transition-colors ${
                    activeTab === "my-notes" 
                      ? "bg-accent border-b-2 border-primary" 
                      : "hover:bg-muted"
                  }`}
                  title="My Notes"
                  data-testid="collapsed-tab-my-notes"
                >
                  <FileText className={`h-4 w-4 ${activeTab === "my-notes" ? "text-primary" : "text-muted-foreground"}`} />
                </button>
                <button
                  onClick={() => setActiveTab("shared")}
                  className={`flex-1 py-2 flex justify-center transition-colors ${
                    activeTab === "shared" 
                      ? "bg-accent border-b-2 border-primary" 
                      : "hover:bg-muted"
                  }`}
                  title="Shared with Me"
                  data-testid="collapsed-tab-shared"
                >
                  <Users className={`h-4 w-4 ${activeTab === "shared" ? "text-primary" : "text-muted-foreground"}`} />
                </button>
              </div>
              
              {/* Collapsed Note List */}
              <ScrollArea className="flex-1">
                <div className="py-2 space-y-1">
                  {activeTab === "my-notes" ? (
                    // My Notes List
                    isLoading ? (
                      <div className="text-center py-4">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                      </div>
                    ) : sortedNotes.length === 0 ? (
                      <div className="text-center py-4">
                        <FileText className="h-6 w-6 mx-auto text-muted-foreground opacity-50" />
                      </div>
                    ) : (
                      sortedNotes.map((note) => (
                        <button
                          key={note.id}
                          onClick={() => handleNoteClick(note)}
                          className={`w-full p-2 flex flex-col items-center gap-1 rounded transition-colors hover:bg-accent ${
                            selectedNote?.id === note.id ? 'bg-accent ring-2 ring-primary' : ''
                          }`}
                          title={note.title}
                          data-testid={`collapsed-note-${note.id}`}
                        >
                          <div className="relative">
                            <FileText className="h-5 w-5 text-muted-foreground" />
                            {note.isPinned && (
                              <Pin className="h-2.5 w-2.5 text-yellow-500 absolute -top-1 -right-1" />
                            )}
                          </div>
                          <span className="text-[9px] text-muted-foreground truncate w-full text-center leading-tight">
                            {note.title.slice(0, 8)}{note.title.length > 8 ? '...' : ''}
                          </span>
                        </button>
                      ))
                    )
                  ) : (
                    // Shared Notes List
                    sharedNotes.length === 0 ? (
                      <div className="text-center py-4">
                        <Users className="h-6 w-6 mx-auto text-muted-foreground opacity-50" />
                      </div>
                    ) : (
                      sharedNotes.map((share: any) => {
                        const note = share.note;
                        if (!note) return null;
                        return (
                          <button
                            key={share.id}
                            onClick={() => handleNoteClick(note)}
                            className={`w-full p-2 flex flex-col items-center gap-1 rounded transition-colors hover:bg-accent ${
                              selectedNote?.id === note.id ? 'bg-accent ring-2 ring-primary' : ''
                            }`}
                            title={note.title}
                            data-testid={`collapsed-shared-note-${note.id}`}
                          >
                            <div className="relative">
                              <FileText className="h-5 w-5 text-muted-foreground" />
                              <Users className="h-2.5 w-2.5 text-blue-500 absolute -top-1 -right-1" />
                            </div>
                            <span className="text-[9px] text-muted-foreground truncate w-full text-center leading-tight">
                              {note.title.slice(0, 8)}{note.title.length > 8 ? '...' : ''}
                            </span>
                          </button>
                        );
                      })
                    )
                  )}
                </div>
              </ScrollArea>
              
              {/* Collapsed Footer Stats */}
              <div className="border-t p-2 flex justify-center">
                <div className="bg-muted/50 rounded p-1.5 text-center">
                  <div className="text-xs font-bold text-primary">
                    {activeTab === "my-notes" ? notes.length : sharedNotes.length}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Notes
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={handleCreateNew}
                    data-testid="button-create-note"
                    className="touch-manipulation min-h-[44px] min-w-[44px]"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">New Note</span>
                    <span className="sm:hidden">New</span>
                  </Button>
                </div>
              </CardHeader>

              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "my-notes" | "shared")} className="flex-1 flex flex-col">
                <div className="px-6">
                  <TabsList className="grid w-full grid-cols-2 h-auto">
                    <TabsTrigger value="my-notes" className="flex items-center gap-2" data-testid="tab-my-notes">
                      <FileText className="h-4 w-4" />
                      My Notes
                    </TabsTrigger>
                    <TabsTrigger value="shared" className="flex items-center gap-2" data-testid="tab-shared-notes">
                      <Users className="h-4 w-4" />
                      Shared with Me
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="my-notes" className="mt-0 flex-1 flex flex-col">
                  <div className="px-6 py-4 space-y-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search notes..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                        data-testid="input-search-notes"
                      />
                    </div>

                    <div className="flex gap-2">
                      <MultiTagSelector
                        availableTags={allTags}
                        selectedTags={selectedTags}
                        onTagsChange={setSelectedTags}
                        triggerButton={
                          <Button
                            variant="outline"
                            className="flex-1 justify-start"
                            data-testid="button-select-tags"
                          >
                            <Filter className="h-4 w-4 mr-2" />
                            Filter by Tags
                            {selectedTags.length > 0 && (
                              <Badge variant="secondary" className="ml-auto">
                                {selectedTags.length}
                              </Badge>
                            )}
                          </Button>
                        }
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setTagManagementOpen(true)}
                        title="Manage Tags"
                        data-testid="button-manage-tags"
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Show selected tags */}
                    {selectedTags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {selectedTags.map(tag => (
                          <Badge 
                            key={tag} 
                            variant="secondary" 
                            className="text-xs cursor-pointer"
                            onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))}
                          >
                            {tag}
                            <X className="h-3 w-3 ml-1" />
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="pinned-only"
                          checked={pinnedOnly}
                          onCheckedChange={setPinnedOnly}
                          data-testid="toggle-pinned-only"
                        />
                        <Label htmlFor="pinned-only" className="text-sm cursor-pointer">
                          Pinned only
                        </Label>
                      </div>
                      {hasActiveFilters && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearFilters}
                          data-testid="button-clear-filters"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>

                  <Separator />

                  <ScrollArea className="flex-1">
                    <div className="p-3 space-y-2">
                      {isLoading ? (
                        <div className="text-center py-8 text-muted-foreground">
                          Loading notes...
                        </div>
                      ) : sortedNotes.length === 0 ? (
                        <div className="text-center py-12 px-4">
                          <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <h3 className="font-semibold mb-2">No notes yet</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            {hasActiveFilters
                              ? "No notes match your filters"
                              : "Create your first note to get started"}
                          </p>
                          {!hasActiveFilters && (
                            <Button 
                              onClick={handleCreateNew} 
                              size="sm"
                              className="touch-manipulation min-h-[44px]"
                              data-testid="button-create-note-empty"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Create Note
                            </Button>
                          )}
                        </div>
                      ) : (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={sortedNotes.map(note => note.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {sortedNotes.map((note) => (
                              <SortableNoteCard key={note.id} note={note} />
                            ))}
                          </SortableContext>
                        </DndContext>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="shared" className="mt-0 flex-1 flex flex-col">
                  <Separator />
                  <ScrollArea className="flex-1">
                    <div className="p-3 space-y-2">
                      {sharedNotes.length === 0 ? (
                        <div className="text-center py-12 px-4">
                          <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <h3 className="font-semibold mb-2">No shared notes</h3>
                          <p className="text-sm text-muted-foreground">
                            Notes shared with you will appear here
                          </p>
                        </div>
                      ) : (
                        sharedNotes.map((share: any) => {
                          const note = share.note;
                          if (!note) return null;
                          
                          const isSelected = selectedNote?.id === note.id;
                          const preview = getContentPreview(note.plainContent || note.content);
                          
                          return (
                            <div
                              key={share.id}
                              onClick={() => handleNoteClick(note)}
                              className={`
                                p-3 rounded-lg border cursor-pointer transition-all
                                hover:border-primary hover:shadow-sm
                                ${isSelected ? 'border-primary bg-accent' : 'border-border'}
                              `}
                              data-testid={`shared-note-item-${note.id}`}
                            >
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <h4 className="font-medium text-sm truncate flex-1">
                                  {note.title}
                                </h4>
                                <Badge variant="outline" className="text-xs">
                                  {share.permission}
                                </Badge>
                              </div>
                              
                              {preview && (
                                <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                                  {preview}
                                </p>
                              )}
                              
                              {note.tags && note.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {note.tags.slice(0, 3).map((tag: string) => (
                                    <Badge
                                      key={tag}
                                      variant="secondary"
                                      className="text-xs px-1.5 py-0"
                                    >
                                      {tag}
                                    </Badge>
                                  ))}
                                  {note.tags.length > 3 && (
                                    <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                      +{note.tags.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              )}
                              
                              <div className="text-xs text-muted-foreground flex items-center justify-between">
                                <span>
                                  Shared by {share.owner?.username || share.owner?.email || 'Unknown'}
                                </span>
                                <span>
                                  {formatDistanceToNow(new Date(share.createdAt), { addSuffix: true })}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </>
          )}
        </Card>
      </div>

      {/* Right Panel - Note Editor */}
      {/* Show on mobile when editing/creating, always show on desktop */}
      <div className={`h-full transition-all duration-300 ease-in-out ${
        (selectedNote || isCreating) ? 'flex flex-col w-full' : 'hidden lg:block'
      } ${sidebarCollapsed ? 'lg:flex-1' : 'w-full lg:w-[70%]'}`}>
        {!selectedNote && !isCreating ? (
          <Card className="h-full flex items-center justify-center">
            <CardContent className="text-center py-12">
              <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No note selected</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Select a note from the list or create a new one
              </p>
              <Button 
                onClick={handleCreateNew}
                className="touch-manipulation min-h-[44px]"
                data-testid="button-create-note-panel"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New Note
              </Button>
            </CardContent>
          </Card>
        ) : (
          <NoteEditor
            note={selectedNote}
            isCreating={isCreating}
            isSaving={isSaving}
            onSave={handleSaveNote}
            onDelete={handleDeleteNote}
            onCancel={handleCancelEdit}
          />
        )}
      </div>

      {/* Tag Management Dialog */}
      <Dialog open={tagManagementOpen} onOpenChange={setTagManagementOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Manage Tags
            </DialogTitle>
            <DialogDescription>
              Add new tags or remove existing ones from all notes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Add new tag */}
            <div className="space-y-2">
              <Label>Add New Tag</Label>
              <div className="flex gap-2">
                <Input
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Enter tag name..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTagName.trim() && !allTags.includes(newTagName.trim())) {
                      addCustomTagMutation.mutate(newTagName.trim());
                    }
                  }}
                  data-testid="input-new-tag"
                />
                <Button
                  onClick={() => {
                    if (newTagName.trim() && !allTags.includes(newTagName.trim())) {
                      addCustomTagMutation.mutate(newTagName.trim());
                    }
                  }}
                  disabled={!newTagName.trim() || allTags.includes(newTagName.trim()) || addCustomTagMutation.isPending}
                  data-testid="button-add-tag"
                >
                  {addCustomTagMutation.isPending ? "Adding..." : "Add"}
                </Button>
              </div>
              {newTagName.trim() && allTags.includes(newTagName.trim()) && (
                <p className="text-xs text-muted-foreground">This tag already exists.</p>
              )}
            </div>

            <Separator />

            {/* Existing tags */}
            <div className="space-y-2">
              <Label>Existing Tags ({allTags.length})</Label>
              {allTags.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No tags yet. Add tags to your notes or create one above.
                </p>
              ) : (
                <ScrollArea className="h-[200px] border rounded-md p-2">
                  <div className="space-y-1">
                    {allTags.map((tag) => (
                      <div
                        key={tag}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-muted group"
                      >
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{tag}</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => removeTagFromAllNotesMutation.mutate(tag)}
                          disabled={removeTagFromAllNotesMutation.isPending}
                          title={`Remove "${tag}" from all notes`}
                          data-testid={`button-delete-tag-${tag}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
